import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { assetsClipCloneCreate } from '@goldcast/api/content';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation } from 'react-query';
import { VideoAssetUsage } from '@goldcast/api/backend';
import { useAllVisibleReels } from './useAllVisibleClips';
import { useClipTitleRename } from './useClipTitleRename';
import useAssetUsage from './useAssetUsage';
import { deleteContentClip } from '@/libs/clipContentUtil';
import { onDuplicateClipSuccess, removeClip } from '@/stores/clip';
import { NavigationEndpoint } from '@/Pages/SessionDetail/types';
import { CustomEvents } from '@/libs/eventBus/constants';
import EventBus from '@/libs/eventBus/eventBus';
import { showErrorToast } from '@/libs/toast/toast';
import { useAppContext } from '@/context/AppContext/AppContext';
import { VideoAssetUsageWithUrl } from '@/Pages/ClipsListPage/useBulkDelete';
import { ClipDeletionError } from '@/Pages/ClipsListPage/ClipDeletionErrorDialog';

// Mirrors the routing table in ClipUsageDialog so ClipBulkDeleteUsageDialog
// can render visit links without requiring ClipsContext.
const USAGE_ROUTES = {
  EVENT: { Recording: '/recording-event', Conference: '/events', Webinar: '/webinar' },
  HUB: '/pages',
  STUDIO_EVENT_TEMPLATE: '/templates'
} as const;

function buildUsageUrl(usage: VideoAssetUsage): string | null {
  const basePath =
    usage.entity_type === 'EVENT'
      ? USAGE_ROUTES.EVENT[usage.entity_subtype as keyof typeof USAGE_ROUTES.EVENT]
      : USAGE_ROUTES[usage.entity_type as keyof typeof USAGE_ROUTES];
  return basePath ? `${basePath}/${usage.entity_id}` : null;
}

export const useReelList = () => {
  const { eventId, broadcastId } = useParams<{ eventId: string; broadcastId: string }>();
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const { logger } = useAppContext();

  const { renamingClip, onSaveNewTitle, closeRenamingClipDialog, setRenamingClip } = useClipTitleRename();
  const { usageData, checkAssetUsage } = useAssetUsage();

  const { allVisibleReels } = useAllVisibleReels();
  // Keep a ref so the delete callback always sees the current list without becoming a dependency.
  const allVisibleReelsRef = useRef(allVisibleReels);
  useEffect(() => {
    allVisibleReelsRef.current = allVisibleReels;
  }, [allVisibleReels]);

  // ── Delete with confirmation ─────────────────────────────────────────────────
  const [pendingDeleteReelId, setPendingDeleteReelId] = useState<string | null>(null);
  const isConfirmDeleteOpen = pendingDeleteReelId !== null;

  const [isReelUsageDialogOpen, setIsReelUsageDialogOpen] = useState(false);
  const closeReelUsageDialog = useCallback(() => setIsReelUsageDialogOpen(false), []);

  const [reelDeletionError, setReelDeletionError] = useState<ClipDeletionError>(null);
  const closeReelDeletionErrorDialog = useCallback(() => setReelDeletionError(null), []);

  // Called by ClipsListItemWithActions — checks video-library usage first.
  // If the reel is in use, shows the usage dialog; otherwise opens the confirm dialog.
  const openDeleteConfirm = useCallback(
    async (reelId: string) => {
      const reel = allVisibleReelsRef.current.find(r => r.id === reelId);
      const videoLibraryId = reel?.asset_metadata?.video_library_id;
      if (videoLibraryId) {
        const usageCount = await checkAssetUsage(videoLibraryId);
        if (usageCount > 0) {
          setIsReelUsageDialogOpen(true);
          return;
        }
      }
      setPendingDeleteReelId(reelId);
    },
    [checkAssetUsage]
  );

  const closeDeleteConfirm = useCallback(() => {
    setPendingDeleteReelId(null);
  }, []);

  const navigateAfterDelete = useCallback(
    (reelId: string) => {
      const reels = allVisibleReelsRef.current;
      const reelIndex = reels.findIndex(reel => reel.id === reelId);
      const prevReelId = reels[reelIndex - 1]?.id;
      const nextReelId = reels[reelIndex + 1]?.id;
      if (prevReelId) {
        setSearchParams({ activeClipId: prevReelId }, { replace: true });
      } else if (nextReelId) {
        setSearchParams({ activeClipId: nextReelId }, { replace: true });
      } else {
        setSearchParams(undefined, { replace: true });
      }
    },
    [setSearchParams]
  );

  // Close the dialog immediately, then wait for the API before fading the reel out.
  // If the delete fails the reel stays visible and the server's error is surfaced.
  const onConfirmDelete = useCallback(() => {
    if (!pendingDeleteReelId) {
      return;
    }
    const reelId = pendingDeleteReelId;
    closeDeleteConfirm();
    deleteContentClip(reelId)
      .then(() => removeClip(reelId).then(() => navigateAfterDelete(reelId)))
      .catch((err: any) => {
        try {
          const parseError = JSON.parse(err);
          setReelDeletionError({ clipId: reelId, message: parseError?.message, textAssets: parseError?.text_assets });
        } catch {
          // Non-API error (e.g. network failure) — fall back to a toast
          showErrorToast('Failed to delete highlight reel');
          logger.error('Failed to delete reel', err);
        }
      });
  }, [pendingDeleteReelId, closeDeleteConfirm, navigateAfterDelete, logger]);

  // Map raw usage entries to include pre-computed visit URLs for ClipBulkDeleteUsageDialog.
  const reelUsageData: VideoAssetUsageWithUrl[] = useMemo(
    () => usageData.map(u => ({ ...u, url: buildUsageUrl(u) })),
    [usageData]
  );

  // ── Duplicate ─────────────────────────────────────────────────────────────────
  const { mutate: duplicateReel } = useMutation(
    (reelId: string) => assetsClipCloneCreate({ id: reelId, body: { asset_id: reelId } }),
    {
      onSuccess: duplicatedReel => {
        onDuplicateClipSuccess(duplicatedReel).then(() => {
          navigate(`/${eventId}/${broadcastId}/${NavigationEndpoint.highlights}`);
          setSearchParams({ activeClipId: duplicatedReel.id }, { replace: true });
        });
      },
      onError: (err: any) => {
        showErrorToast('Failed to duplicate highlight reel. Please try again.');
        logger.error('Failed to duplicate reel', err);
      }
    }
  );

  // ── Social post ───────────────────────────────────────────────────────────────
  const onGenerateSocialPost = useCallback(
    (reelId: string) => {
      navigate(`/${eventId}/${broadcastId}/${NavigationEndpoint.social}`, { state: { clipId: reelId } });
      EventBus.dispatch(CustomEvents.HighlightListPopUp, { isOpen: false });
    },
    [navigate, eventId, broadcastId]
  );

  return {
    // delete
    openDeleteConfirm,
    isConfirmDeleteOpen,
    closeDeleteConfirm,
    onConfirmDelete,
    reelUsageData,
    isReelUsageDialogOpen,
    closeReelUsageDialog,
    reelDeletionError,
    closeReelDeletionErrorDialog,
    setRenamingClip,
    renamingClip,
    onSaveNewTitle,
    closeRenamingClipDialog,
    duplicateReel,
    onGenerateSocialPost
  };
};
